import React, { useEffect, useRef, useCallback } from "react";
import { ChevronRight } from "lucide-react";
import { STAGE_COLORS, formatCurrency } from "./utils";

/**
 * StatCard — top row metric tile. Clickable variant adds a colored top
 * stripe + persistent "View →" affordance so users know it drills in.
 */
/**
 * StatValue — compact inline metric for the hero strip.
 * Clickable variant (Blocked, Overdue) shows accent color + "view" badge.
 */
// caption / children / big: the ERP dashboard hero (2-Nov-2026) adds a short
// plain-words caption and, for Active items, a tiny stage-split bar.
export function StatValue({ label, value, color, testId, onClick, caption, big = false, children }) {
  const clickable = typeof onClick === "function";
  const inner = (
    <div className="relative flex flex-col gap-0.5">
      <span className="flex items-center gap-1.5">
        <span className={`text-[10px] font-medium uppercase tracking-[0.08em] ${clickable ? "" : "text-[hsl(var(--muted-foreground))]/70"}`} style={clickable ? { color } : undefined}>{label}</span>
        {clickable && (
          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-px rounded" style={{ background: color + "15", color }}>view</span>
        )}
      </span>
      <span className={`text-[1.5rem] ${big ? "md:text-[1.75rem] md:mt-1" : ""} font-bold font-mono-data tracking-tight leading-none ${clickable ? "" : "text-[hsl(var(--foreground))]"}`} style={clickable ? { color } : undefined}>{value}</span>
      {caption && <span className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 leading-tight">{caption}</span>}
      {children}
    </div>
  );

  if (clickable) {
    return (
      <button type="button" onClick={onClick} className="text-left cursor-pointer hover:opacity-80 transition-opacity" data-testid={testId}>
        {inner}
      </button>
    );
  }
  return <div data-testid={testId}>{inner}</div>;
}

/**
 * PipelineDots — canvas-rendered dot grid that fills upward proportionally
 * (relative to maxCount). Animates in with a diagonal sweep on first mount.
 */
export function PipelineDots({ count, color, maxCount, containerRef }) {
  const canvasRef = useRef(null);
  const animatedRef = useRef(false);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef?.current;
    if (!canvas || !container || count <= 0 || !maxCount) return;

    const W = container.offsetWidth;
    const totalH = container.offsetHeight;
    const dpr = window.devicePixelRatio || 1;

    const DOT_R = 1.4;
    const GAP = 2.8;
    const STEP = DOT_R * 2 + GAP;
    const padLeft = 8;
    const cols = Math.floor((W - padLeft - 4) / STEP);
    if (cols <= 0) return;

    const fillRatio = Math.sqrt(count / maxCount);
    const maxFillH = totalH * 0.97;
    const minFillH = Math.max(totalH * 0.15, STEP * 3);
    const canvasH = minFillH + (maxFillH - minFillH) * fillRatio;

    canvas.width = W * dpr;
    canvas.height = canvasH * dpr;
    canvas.style.width = W + "px";
    canvas.style.height = canvasH + "px";

    // Clamp so fade never covers more than the bottom 35% of the canvas.
    // Without this guard, small canvases (low-count stages) get textZoneTop < 0,
    // making the fade condition always-true and wiping all dots to min-alpha.
    const textZoneTop = Math.max(canvasH * 0.65, canvasH - 44);
    const textZoneRight = W * 0.65;
    const sparkZoneLeft = W * 0.55;
    const sparkZoneTop = Math.max(canvasH * 0.75, canvasH - 28);
    const fadeMargin = 12;

    const drawRows = Math.ceil(canvasH / STEP);

    const dots = [];
    let dotIndex = 0;
    for (let r = drawRows - 1; r >= 0; r--) {
      for (let c = 0; c < cols; c++) {
        if (dotIndex >= count) break;
        const x = padLeft + c * STEP + DOT_R;
        const y = r * STEP + DOT_R;
        if (y > canvasH) continue;

        let alpha = 0.32;
        if (y > textZoneTop - fadeMargin && x < textZoneRight + fadeMargin) {
          const dy = Math.min(1, Math.max(0, y - (textZoneTop - fadeMargin)) / (canvasH - textZoneTop + fadeMargin));
          const dx = Math.min(1, Math.max(0, 1 - (x - textZoneRight) / fadeMargin));
          const proximity = dy * dx;
          alpha = Math.max(0.06, 0.32 - proximity * 0.26);
        }
        if (y > sparkZoneTop - fadeMargin && x > sparkZoneLeft - fadeMargin) {
          const dy = Math.min(1, Math.max(0, y - (sparkZoneTop - fadeMargin)) / (canvasH - sparkZoneTop + fadeMargin));
          const dx = Math.min(1, Math.max(0, (x - (sparkZoneLeft - fadeMargin)) / (W - sparkZoneLeft + fadeMargin)));
          const proximity = Math.min(1, dy * dx * 4);
          const sparkAlpha = Math.max(0.06, 0.32 - proximity * 0.26);
          alpha = Math.min(alpha, sparkAlpha);
        }

        const nx = x / W;
        const ny = 1 - (y / canvasH);
        const diag = (nx + ny) / 2;

        dots.push({ x, y, alpha, diag });
        dotIndex++;
      }
      if (dotIndex >= count) break;
    }

    dots.sort((a, b) => a.diag - b.diag);

    const shouldAnimate = !animatedRef.current;

    if (!shouldAnimate) {
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.fillStyle = color;
      for (const dot of dots) {
        ctx.globalAlpha = dot.alpha;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, DOT_R, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }

    animatedRef.current = true;
    const DELAY = 150;
    const DURATION = 500;
    let startTime = null;
    let rafId = null;

    function easeOut(t) { return 1 - (1 - t) * (1 - t); }

    function frame(ts) {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime - DELAY;
      const progress = elapsed <= 0 ? 0 : easeOut(Math.min(1, elapsed / DURATION));

      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, canvasH);
      ctx.fillStyle = color;

      for (const dot of dots) {
        if (dot.diag > progress) continue;
        const localT = Math.min(1, (progress - dot.diag) / 0.12);
        ctx.globalAlpha = dot.alpha * localT;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, DOT_R, 0, Math.PI * 2);
        ctx.fill();
      }

      if (progress < 1) {
        rafId = requestAnimationFrame(frame);
      }
    }
    rafId = requestAnimationFrame(frame);

    return () => { if (rafId) cancelAnimationFrame(rafId); };
  }, [count, color, maxCount, containerRef]);

  useEffect(() => {
    let id = requestAnimationFrame(() => {
      id = requestAnimationFrame(draw);
    });
    return () => cancelAnimationFrame(id);
  }, [draw]);

  if (count <= 0) return null;
  return <canvas ref={canvasRef} style={{ position: "absolute", bottom: 0, left: 0 }} />;
}

/**
 * StageSparkline — tiny SVG line for 7-day stage trend. No recharts overhead
 * for this 64×22 visualization.
 */
export function StageSparkline({ data = [], color, width = 64, height = 22 }) {
  if (!Array.isArray(data) || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padX = 1, padY = 2;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const step = innerW / (data.length - 1);
  const points = data.map((v, i) => {
    const x = padX + i * step;
    const y = padY + innerH - ((v - min) / range) * innerH;
    return [x, y];
  });
  const pathLine = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const last = points[points.length - 1];
  const direction = data[data.length - 1] === data[0] ? "flat" : (data[data.length - 1] > data[0] ? "up" : "down");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${data.length}-day trend: ${direction}`} style={{ overflow: "visible" }}>
      <line x1={padX} x2={width - padX} y1={padY + innerH} y2={padY + innerH} stroke="hsl(var(--muted-foreground))" strokeOpacity="0.25" strokeWidth="1" />
      <path d={pathLine} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="1.8" fill={color} />
    </svg>
  );
}

/** PipelineStageCard — the colored stage tile in the pipeline overview row. */
export function PipelineStageCard({ stage, count, value, onClick, showValue, trendData, maxCount }) {
  const sc = STAGE_COLORS[stage];
  const ratio = maxCount ? Math.min(1, count / maxCount) : 0;
  // Wash opacity scales with the stage's share of the busiest stage so a glance
  // conveys relative load. Range 4%→34% (was 5%→20%, too narrow to read), with a
  // gentle ratio^0.7 curve: keeps low-count stages faint-but-visible while
  // letting the heaviest stage read clearly stronger (~6× contrast end-to-end).
  const washHex = count > 0 ? Math.round((0.04 + 0.30 * Math.pow(ratio, 0.7)) * 255).toString(16).padStart(2, "0") : "00";
  return (
    <button
      onClick={onClick}
      className="flex flex-col h-[110px] rounded-lg rl-pipeline-card transition-all cursor-pointer text-left group relative overflow-hidden bg-[hsl(var(--card))]"
      style={{ backgroundImage: `radial-gradient(95% 110% at 0% 0%, ${sc.color}${washHex}, transparent 70%)` }}
      data-testid={`pipeline-card-${stage.toLowerCase().replace(/\s/g, '-')}`}
    >
      {/* Straight left rail — no rounding */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] z-[3]" style={{ background: sc.color }} />
      {/* Stage name — pinned top */}
      <div className="flex items-center justify-between gap-1 px-3 pt-2.5 relative z-10">
        <span className="text-[11px] font-semibold tracking-[0.01em]" style={{ color: sc.color }}>{stage}</span>
        <ChevronRight className="w-3 h-3 shrink-0 opacity-40 group-hover:opacity-80 group-hover:translate-x-0.5 transition-all" style={{ color: sc.color }} />
      </div>

      {/* Body — proportional stage-color wash sits on the card; text at bottom */}
      <div className="flex-1 relative overflow-hidden">
        {/* Count + value + sparkline */}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-1 z-10">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold font-mono-data leading-none">{count}</span>
              <span className="text-[10px] text-[hsl(var(--muted-foreground))] leading-none pb-0.5">items</span>
            </div>
            {showValue && (
              <span className="text-[10px] font-mono-data text-[hsl(var(--muted-foreground))] tabular-nums">{formatCurrency(value)}</span>
            )}
          </div>
          {trendData && trendData.length >= 2 && (
            <div className="opacity-70 group-hover:opacity-100 transition-opacity shrink-0" data-testid={`pipeline-spark-${stage.toLowerCase().replace(/\s/g, '-')}`}>
              <StageSparkline data={trendData} color={sc.color} />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
